import styled from '@emotion/styled';

import COLOR from '../../../../../../shared/constant/colorset';

import { addComma } from '../../../../../../shared/services';
import { PriceService } from '../../../../../service/priceService';

import { DealProduct, DirectOrderType } from '../../../../types';

import RemoveButton from './RemoveButton';
import useDealProduct from '../../../../hooks/useDealProduct';
import DealProductStepper from '../../../../shared/DealProductStepper';

const Container = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 50px;
  background-color: ${COLOR.bgLightGray};
  padding: 10px 0 10px 20px;
`;

const ProductNameWrapper = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 500px;
  margin-right: 40px;
`;

const ProductName = styled.span<{ disabled: boolean; }>`
  line-height: 1.36;
  letter-spacing: -0.5px;
  color: ${({ disabled }) => (disabled ? COLOR.kurlyGray350 : COLOR.kurlyGray800)};
  max-width: 500px;
`;

const ExcludedEarnText = styled.span<{ disabled: boolean; }>`
  width: 320px;
  font-size: 12px;
  line-height: 1.33;
  letter-spacing: -0.3px;
  color: ${({ disabled }) => (disabled ? COLOR.kurlyGray350 : COLOR.kurlyGray600)};
  margin-top: 4px;
`;

const ProductSelectWrapper = styled.span`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: center;
`;

const StepperWrapper = styled(DealProductStepper)`
  background-color: ${COLOR.kurlyWhite};
  margin-right: 19px;
`;

const PriceWrapper = styled.span<{ isDisplayRemove: boolean; }>`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  justify-content: flex-end;
  padding-right: ${(props => props.isDisplayRemove ? '12px' : '20px')};
`;

const OriginPrice = styled.span<{ disabled: boolean; }>`
  text-decoration: line-through;
  line-height: 1.43;
  letter-spacing: -0.5px;
  color: ${({ disabled }) => (disabled ? COLOR.kurlyGray350 : COLOR.kurlyGray400)};
`;

const DiscountedPrice = styled.span<{ disabled: boolean; }>`
  font-weight: bold;
  line-height: 1.43;
  letter-spacing: -0.5px;
  color: ${({ disabled }) => (disabled ? COLOR.kurlyGray350 : COLOR.kurlyGray800)};
  margin-left: 6px;
`;

interface Props {
  dealProduct: DealProduct;
  isDisplayRemove?: boolean;
  directOrderType: DirectOrderType;
}

export default function SelectedProductItem({
  dealProduct,
  isDisplayRemove = false,
  directOrderType,
}: Props) {
  const { deselect } = useDealProduct(dealProduct);

  const {
    name, isSoldOut, isPurchaseStatus, retailPrice, basePrice, discountedPrice,
  } = dealProduct;

  const {
    representativePrice,
    kurlyPrice,
    originalPrice,
  } = new PriceService({ retailPrice, basePrice, discountedPrice });

  const notSalePrice = (kurlyPrice ? kurlyPrice : originalPrice) ?? 0;

  const displayExpectedPointGuide = !dealProduct.isExpectedPoint;

  const disabled = isSoldOut || !isPurchaseStatus;

  return (
    <Container>
      <ProductNameWrapper>
        <ProductName disabled={disabled}>
          {isSoldOut && '(품절) '}
          {!isPurchaseStatus && '(구매불가) '}
          {name}
        </ProductName>
        {displayExpectedPointGuide && (
          <ExcludedEarnText disabled={disabled}>
            적립제외상품
          </ExcludedEarnText>
        )}
      </ProductNameWrapper>
      <ProductSelectWrapper>
        {!disabled && (
          <StepperWrapper
            dealProduct={dealProduct}
            directOrderType={directOrderType}
          />
        )}
        <PriceWrapper isDisplayRemove={isDisplayRemove}>
          {!!notSalePrice && (
            <OriginPrice disabled={disabled}>
              {addComma(notSalePrice)}원
            </OriginPrice>
          )}
          <DiscountedPrice disabled={disabled}>
            {addComma(representativePrice)}원
          </DiscountedPrice>
          {isDisplayRemove && (
            <RemoveButton onClick={deselect} />
          )}
        </PriceWrapper>
      </ProductSelectWrapper>
    </Container>
  );
}
